/**
 * Logout reason storage helpers.
 *
 * The api-client's `on401` handler can stash a short, human-readable
 * reason into sessionStorage under `logout_reason` so that the next
 * page (typically the login screen) can surface it via the
 * `LogoutReasonBanner` component.
 *
 * The current `on401` implementations in `lib/api-client.ts` do not
 * yet write to this key — they only clear localStorage tokens and
 * redirect to `/login`. This module exists so the banner component
 * compiles and so a future enhancement can wire the storage write
 * without a component refactor.
 */

export type LogoutReasonKey =
  | 'account_inactive'
  | 'token_invalid'
  | 'session_expired'
  | 'idle_timeout'
  | 'unknown';

const STORAGE_KEY = 'logout_reason';

/**
 * Read the raw reason string previously stashed by the `on401`
 * handler. Returns `null` if nothing was stashed or if `sessionStorage`
 * is unavailable (e.g. server-side render).
 */
export function getLogoutReason(): string | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Stash a logout reason into sessionStorage.
 */
export function setLogoutReason(reason: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, reason);
  } catch {
    // Ignore — storage may be disabled (private mode, quota errors).
  }
}

/**
 * Unconditionally clear the stashed reason. Safe to call when nothing
 * was stashed.
 */
export function clearLogoutReason(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — storage may be disabled (private mode, quota errors).
  }
}

/**
 * Map the raw 401 reason text (free-form, comes from the API error
 * body) to one of the known `LogoutReasonKey` values used by the
 * `copy.loginBanner` map. Returns `null` if the raw input is empty
 * or unrecognised so the banner stays hidden.
 */
export function resolveLogoutReason(raw: string | null): LogoutReasonKey | null {
  if (!raw) return null;
  const normalised = raw.toLowerCase();

  if (
    normalised.includes('idle') ||
    normalised.includes('inactivity') ||
    normalised.includes('tidak ada aktivitas') ||
    normalised.includes('inaktif')
  ) {
    return 'idle_timeout';
  }

  if (
    normalised.includes('tidak aktif') ||
    normalised.includes('inactive') ||
    normalised.includes('deactivated') ||
    normalised.includes('not active')
  ) {
    return 'account_inactive';
  }

  if (
    normalised.includes('refresh') ||
    normalised.includes('expired') ||
    normalised.includes('kadaluarsa')
  ) {
    return 'session_expired';
  }

  if (
    normalised.includes('token') ||
    normalised.includes('unauthorized') ||
    normalised.includes('tidak valid')
  ) {
    return 'token_invalid';
  }

  return 'unknown';
}
